import * as React from 'react';
import { useIntl } from 'react-intl';

import {
  Text,
  IconRegularDrop,
  IconRegularSun,
} from '@notino/react-styleguide';

import { useAnimateUnmount } from '@components/Animate/useAnimateUnmount';

import { SHADEFINDER_CONFIG } from '../../config';
import { useSaveShadefinderUserData } from '../../hooks/useSaveShadefinderUserData';
import { useShadefinderTransitions } from '../../hooks/useShadefinderTransitions';
import { useTrackSelect } from '../../hooks/useTrackSelect';
import { messages } from '../../messages';
import { trackShadefinderSelectUndertone } from '../../tracking';
import {
  ShadefinderDesiredShade,
  ShadefinderShadeUndertone,
  ShadefinderShadesKeys,
  ShadefinderShadeUndertoneKeys,
} from '../../types';
import { Item } from '../Item';
import { StepContainer } from '../StepContainer';
import { Center } from '../styled';

import { ShadefinderStep3 } from './Step3';

export interface IShadefinderStep2Props {
  masterId: string;
  undertones: ShadefinderShadeUndertone;
  shade: ShadefinderShadesKeys;
  timeOfStart: number;
}

const STEP_3_PROPS = {
  currentStep: 3,
  name: 'select_tone',
};

export const ShadefinderStep2: React.FC<IShadefinderStep2Props> = ({
  masterId,
  undertones,
  shade,
  timeOfStart,
}) => {
  const { formatMessage } = useIntl();
  const { onShadefinderUserDataChanged } = useSaveShadefinderUserData(masterId);
  const { transition, animateProgress } = useShadefinderTransitions();
  const [unmounting, animateTransition] = useAnimateUnmount();
  const trackSelect = useTrackSelect(timeOfStart);

  const renderableOrderedUndertones = Object.keys(
    SHADEFINDER_CONFIG.undertone
  ).filter((key) => key in undertones);

  return (
    <StepContainer
      unmounting={unmounting}
      header={formatMessage(messages.chooseSkinUndertoneTitle)}
      subheader={formatMessage(messages.chooseSkinUndertoneDescription)}
    >
      {renderableOrderedUndertones.map(
        (undertone: ShadefinderShadeUndertoneKeys) => {
          const desiredShades = undertones[
            undertone
          ] as ShadefinderDesiredShade;
          const imageUrl = SHADEFINDER_CONFIG.shades[shade].imageUrl;

          const undertoneConfig = SHADEFINDER_CONFIG.undertone[undertone];
          const background = undertoneConfig.color;
          const title = formatMessage(undertoneConfig.title);
          const indicatorText = formatMessage(undertoneConfig.indicatorText);
          const description = formatMessage(undertoneConfig.description);

          const id = `undertone-${undertone}`;
          return (
            <Item
              key={undertone}
              imageUrl={imageUrl}
              labelledby={id}
              background={background}
              onClick={animateTransition(
                () =>
                  transition(
                    <ShadefinderStep3
                      masterId={masterId}
                      desiredShades={desiredShades}
                      timeOfStart={timeOfStart}
                    />,
                    STEP_3_PROPS
                  ),
                {
                  beforeTransition: () => {
                    void onShadefinderUserDataChanged({
                      skinUnderTone: undertone,
                    });
                    animateProgress(STEP_3_PROPS);
                    trackSelect((props) =>
                      trackShadefinderSelectUndertone({
                        ...props,
                        name: undertone,
                      })
                    );
                  },
                }
              )}
            >
              <Text
                typography="labelLarge400"
                block={true}
                color="text.primary"
                marginBottom="0.25rem"
              >
                {title}
              </Text>

              <Center>
                <IconRegularDrop width="0.945rem" height="0.945rem" />{' '}
                <Text id={id} typography="labelRegular400" marginLeft="0.5rem">
                  {indicatorText}
                </Text>
              </Center>

              <Center padding="0.25rem 0">
                <IconRegularSun width="1rem" height="1rem" />{' '}
                <Text typography="labelRegular400" marginLeft="0.5rem">
                  {description}
                </Text>
              </Center>
            </Item>
          );
        }
      )}
    </StepContainer>
  );
};
